import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonSelect,
  Loader,
  MenuMulti,
  Typography,
} from '@saviynt/design-system';

import { type ActiveFilters } from './helpers/filterUtils';

import './FilterPills.css';

// Types for filter configuration
export interface FilterOption {
  label: string;
  value: string;
  isChecked?: boolean;
  isDisabled?: boolean;
}

export interface FilterPill {
  id: string;
  label: string;
  options: FilterOption[];
}

interface FilterPillsProps {
  isVisible: boolean;
  filterPills?: FilterPill[];
  activeFilters?: ActiveFilters; // Current filter selections - enables controlled component pattern
  isLoading?: boolean;
  error?: string | null;
  onFilterChange: (filterId: string, selectedOptions: FilterOption[]) => void;
  onClearAll?: () => void;
}

const defaultProps: Partial<FilterPillsProps> = {
  filterPills: [],
  activeFilters: { applications: [], entitlements: [], access_status: [] },
  isLoading: false,
  error: null,
  onClearAll: undefined,
};

function FilterPills({
  isVisible,
  filterPills = [],
  activeFilters = { applications: [], entitlements: [], access_status: [] },
  isLoading = false,
  error = null,
  onFilterChange,
  onClearAll,
}: FilterPillsProps): JSX.Element | null {
  // Internal state for managing pill selections
  const [internalFilterPills, setInternalFilterPills] =
    useState<FilterPill[]>(filterPills);

  // Controlled component pattern: Sync internal state with parent's activeFilters
  // This ensures UI always reflects the actual filter state (single source of truth)
  useEffect(() => {
    if (filterPills.length > 0) {
      // Create pills with selection state based on activeFilters
      const syncedPills = filterPills.map((pill) => ({
        ...pill,
        options: pill.options.map((option) => ({
          ...option,
          // Set isChecked based on whether this option is in activeFilters
          isChecked: activeFilters[pill.id as keyof ActiveFilters].includes(
            option.value
          ),
        })),
      }));

      setInternalFilterPills(syncedPills);
    }
  }, [filterPills, activeFilters]);

  // Handle selection changes for a specific filter pill
  const handlePillChange =
    (filterId: string) => (selectedOptions?: FilterOption[]) => {
      if (!selectedOptions) {
        return;
      }

      // Update internal state
      setInternalFilterPills((prevPills) =>
        prevPills.map((pill) =>
          pill.id === filterId
            ? {
                ...pill,
                options: pill.options.map((option) => ({
                  ...option,
                  isChecked: selectedOptions.some(
                    (selected) => selected.value === option.value
                  ),
                })),
              }
            : pill
        )
      );

      // Notify parent component of changes
      onFilterChange(filterId, selectedOptions);
    };

  // Check if any filters are active
  const hasActiveFilters =
    activeFilters.applications.length > 0 ||
    activeFilters.entitlements.length > 0 ||
    activeFilters.access_status.length > 0;

  return (
    <Box
      className={`Rules-FilterPills ${
        isVisible
          ? 'Rules-FilterPills--isVisible'
          : 'Rules-FilterPills--isHidden'
      }`}
      aria-hidden={!isVisible}>
      <Box className='Rules-FilterPills-container'>
        {isLoading && (
          <Box className='Rules-FilterPills-loading'>
            <Loader kind='loop' format='inline' color='brand' size='small' />
            <Typography kind='body2' color='--color-foreground-neutral-subtle'>
              Loading filter options...
            </Typography>
          </Box>
        )}

        {error && !isLoading && (
          <Box className='Rules-FilterPills-error'>
            <Typography kind='body2' color='--color-foreground-critical'>
              Failed to load filter options: {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && internalFilterPills.length > 0 && (
          <>
            {internalFilterPills.map((pill) => (
              <Box
                key={pill.id}
                className={`Rules-FilterPills-pill Rules-FilterPills-pill--${pill.id}`}>
                <MenuMulti
                  options={pill.options}
                  label={pill.label}
                  onChange={handlePillChange(pill.id)}
                  trigger={<ButtonSelect kind='pill' label='' />}
                  size='medium'
                />
              </Box>
            ))}

            {hasActiveFilters && onClearAll && (
              <Button
                className='Rules-FilterPills-clearAll'
                onClick={onClearAll}
                kind='ghost'
                size='small'>
                Clear
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

FilterPills.defaultProps = defaultProps;

export default FilterPills;
